import { useEffect, useState, useRef } from "react";
import {
  AXIS_Y,
  BOX_W,
  BOX_X,
  CARD_COLOR,
  BOLETO_COLOR,
  BOLETO_Y,
  CARD_RELEASE,
  CYCLE,
  DAYS,
  DOCK,
  EVENT_END,
  Frame,
  GREEN,
  GREEN_TEXT,
  HOLD,
  PANEL_X,
  PIX_Y,
  CARD_Y,
  RUN,
  SALES,
  T0,
  T_STEP,
  WITHDRAWAL_EVENTS,
  brl,
  computeFrame,
  dayX,
} from "./withdrawal-rules-timeline";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (ev: MediaQueryListEvent) => setReduced(ev.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function Lock({ opacity }: { opacity: number }) {
  if (opacity <= 0.01) return null;
  return (
    <g transform="translate(0 14)" opacity={opacity}>
      <rect x={-3.5} y={-1} width={7} height={6} rx={1.4} fill={CARD_COLOR} />
      <path d="M-2 -1 v-2 a2 2 0 0 1 4 0 v2" fill="none" stroke={CARD_COLOR} strokeWidth={1.1} />
    </g>
  );
}

export function WithdrawalRulesDemo() {
  const { t } = useTranslation("producerFinancial");
  const reduced = usePrefersReducedMotion();
  const [frame, setFrame] = useState<Frame>(() => computeFrame(0));
  const [scrubDay, setScrubDay] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const animTimeRef = useRef<number>(0);
  const lastTsRef = useRef<number | null>(null);

  // Animation loop
  useEffect(() => {
    if (reduced || scrubDay !== null) return;
    let raf = 0;
    const loop = (ts: number) => {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;
      animTimeRef.current = (animTimeRef.current + delta) % CYCLE;
      setFrame(computeFrame(animTimeRef.current));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lastTsRef.current = null;
    };
  }, [reduced, scrubDay]);

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 520;
    // Permitir scrub em toda a extensão do eixo temporal
    if (x >= 20 && x <= 365) {
      const d = Math.max(0, Math.min(DAYS, (x - T0) / T_STEP));
      setScrubDay(d);
      const END_DAY = DAYS + 0.5;
      animTimeRef.current = (d / END_DAY) * RUN;
      setFrame(computeFrame(animTimeRef.current));
    }
  };

  const handlePointerLeave = () => {
    setScrubDay(null);
    lastTsRef.current = null;
  };

  const { clock, coins, available, toRelease, withdrawn, landPulse, unlockPulse, stage, finished } = frame;
  const headX = dayX(Math.min(clock, DAYS));
  const eventX = dayX(EVENT_END);
  const releaseX = dayX(CARD_RELEASE);
  const dockScale = 1 + landPulse * 0.025;

  return (
    <div className="w-full space-y-3 rounded-3xl border border-border/50 bg-secondary/15 p-3.5 sm:p-5 shadow-xs">
      {/* SVG Canvas da Linha do Tempo */}
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-background/80 shadow-xs">
        <svg
          ref={svgRef}
          viewBox="0 0 520 286"
          className="w-full cursor-crosshair select-none touch-pan-y"
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
        >
          <defs>
            <filter id="wdShadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.14" />
            </filter>
            <linearGradient id="eventBandGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.12" />
              <stop offset="100%" stopColor="hsl(142 71% 45%)" stopOpacity="0.15" />
            </linearGradient>
          </defs>

          <g opacity={reduced ? 1 : stage}>
            {/* ── Palco da linha do tempo ── */}
            <rect
              x={14}
              y={14}
              width={344}
              height={258}
              rx={12}
              fill="hsl(var(--background))"
              stroke="hsl(var(--border))"
              strokeWidth={1.5}
            />

            {/* Faixa destacada: Fim do evento até Liberação de Cartão (D+7 a D+8) */}
            <rect
              x={eventX}
              y={32}
              width={releaseX - eventX}
              height={AXIS_Y - 48}
              fill="url(#eventBandGrad)"
              stroke="hsl(var(--primary))"
              strokeOpacity={0.3}
              strokeDasharray="2 2"
            />

            {/* Marco do fim do evento */}
            <line x1={eventX} y1={36} x2={eventX} y2={AXIS_Y - 8} stroke="hsl(var(--primary))" strokeWidth={1.4} strokeDasharray="3 3" />
            <g transform={`translate(${eventX} 30)`}>
              <rect x={-35} y={-9} width={70} height={14} rx={7} fill="hsl(var(--primary))" />
              <text y={1} textAnchor="middle" className="text-[6.5px] font-bold" fill="hsl(var(--primary-foreground))">
                Fim do evento
              </text>
            </g>

            {/* Janela pós-evento (a partir de D+8) */}
            <rect x={releaseX} y={32} width={dayX(DAYS) - releaseX} height={AXIS_Y - 48} fill={GREEN} opacity={0.06} />

            {/* faixa PIX */}
            <text x={24} y={PIX_Y - 25} className="text-[9px] font-bold" fill="hsl(var(--primary))">
              {t("withdrawalRules.demo.pix")}
            </text>
            <text x={24} y={PIX_Y - 15} className="text-[7px]" fill="hsl(var(--muted-foreground))">
              {t("withdrawalRules.demo.pixReleases")}
            </text>
            <line x1={T0 - 6} y1={PIX_Y} x2={dayX(DAYS)} y2={PIX_Y} stroke="hsl(var(--border))" strokeOpacity={0.7} />

            {/* faixa cartão */}
            <text x={24} y={CARD_Y - 25} className="text-[9px] font-bold" fill={CARD_COLOR}>
              {t("withdrawalRules.demo.card")}
            </text>
            <text x={24} y={CARD_Y - 15} className="text-[7px]" fill="hsl(var(--muted-foreground))">
              {t("withdrawalRules.demo.afterEvent")}
            </text>
            <line x1={T0 - 6} y1={CARD_Y} x2={dayX(DAYS)} y2={CARD_Y} stroke="hsl(var(--border))" strokeOpacity={0.7} />

            {/* faixa boleto */}
            <text x={24} y={BOLETO_Y - 25} className="text-[9px] font-bold" fill={BOLETO_COLOR}>
              {t("withdrawalRules.demo.boleto")}
            </text>
            <text x={24} y={BOLETO_Y - 15} className="text-[7px]" fill="hsl(var(--muted-foreground))">
              {t("withdrawalRules.demo.afterEvent")}
            </text>
            <line x1={T0 - 6} y1={BOLETO_Y} x2={dayX(DAYS)} y2={BOLETO_Y} stroke="hsl(var(--border))" strokeOpacity={0.7} />

            {/* aviso do destravamento do cartão */}
            {unlockPulse > 0.01 && (
              <g opacity={unlockPulse}>
                <line x1={releaseX} y1={CARD_Y - 21} x2={releaseX} y2={BOLETO_Y - 9} stroke={GREEN} strokeWidth={1.4} />
                <text x={releaseX} y={CARD_Y - 25} textAnchor="middle" className="text-[7.5px] font-bold" fill={GREEN_TEXT}>
                  {t("withdrawalRules.demo.releaseAll")}
                </text>
              </g>
            )}

            {/* Marcadores de Retirada de Saldo (Saques Efetuados) */}
            {WITHDRAWAL_EVENTS.map((w) => {
              const wx = dayX(w.day);
              const isPast = clock >= w.day;
              return (
                <g key={w.id} opacity={isPast ? 1 : 0.45}>
                  <line
                    x1={wx}
                    y1={AXIS_Y - 22}
                    x2={wx}
                    y2={AXIS_Y}
                    stroke={isPast ? "#F59E0B" : "hsl(var(--border))"}
                    strokeWidth={1.2}
                    strokeDasharray={isPast ? undefined : "2 2"}
                  />
                  <circle cx={wx} cy={AXIS_Y} r={3} fill={isPast ? "#F59E0B" : "hsl(var(--muted))"} />
                  <g transform={`translate(${wx} ${AXIS_Y - 19})`}>
                    <rect
                      x={-25}
                      y={-7}
                      width={50}
                      height={13}
                      rx={6.5}
                      fill={isPast ? "#FEF3C7" : "hsl(var(--card))"}
                      stroke={isPast ? "#F59E0B" : "hsl(var(--border))"}
                      strokeWidth={1}
                    />
                    <text y={2.5} textAnchor="middle" className="text-[6.5px] font-bold" fill={isPast ? "#92400E" : "hsl(var(--muted-foreground))"}>
                      💸 -{brl(w.amount)}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Eixo temporal */}
            <line x1={T0 - 6} y1={AXIS_Y} x2={dayX(DAYS)} y2={AXIS_Y} stroke="hsl(var(--border))" />
            {[0, 2, 4, 6, 8, 10].map((d) => (
              <g key={d}>
                <line x1={dayX(d)} y1={AXIS_Y} x2={dayX(d)} y2={AXIS_Y + 4} stroke="hsl(var(--border))" />
                <text
                  x={dayX(d)}
                  y={AXIS_Y + 14}
                  textAnchor="middle"
                  className="text-[7px] tabular-nums"
                  fill="hsl(var(--muted-foreground))"
                >
                  D+{d}
                </text>
              </g>
            ))}

            {/* Cursor "Hoje" / Linha interativa */}
            <g>
              <line x1={headX} y1={36} x2={headX} y2={AXIS_Y} stroke="hsl(var(--primary))" strokeWidth={1.5} opacity={scrubDay !== null ? 0.9 : 0.6} />
              <circle cx={headX} cy={AXIS_Y} r={3.5} fill="hsl(var(--primary))" />
              <rect x={headX - 16} y={AXIS_Y + 7} width={32} height={14} rx={7} fill="hsl(var(--primary))" />
              <text x={headX} y={AXIS_Y + 17} textAnchor="middle" className="text-[7px] font-bold" fill="hsl(var(--primary-foreground))">
                {scrubDay !== null ? `D+${scrubDay.toFixed(1)}` : t("withdrawalRules.demo.today")}
              </text>
            </g>

            {/* Tooltip flutuante ao passar o mouse */}
            {scrubDay !== null && (
              <g transform={`translate(${Math.max(70, Math.min(290, headX))} 24)`}>
                <rect x={-62} y={-10} width={124} height={18} rx={9} fill="hsl(var(--foreground))" opacity={0.92} />
                <text y={2} textAnchor="middle" className="text-[7px] font-bold" fill="hsl(var(--background))">
                  Dia {scrubDay.toFixed(1)} · Disponível: {brl(available)}
                </text>
              </g>
            )}

            {/* ── Painel da direita ── */}
            <rect
              x={PANEL_X}
              y={14}
              width={134}
              height={258}
              rx={12}
              fill="hsl(var(--secondary))"
              fillOpacity={0.5}
              stroke="hsl(var(--border))"
              strokeOpacity={0.6}
            />

            {/* Disponível */}
            <g transform={`translate(${DOCK.x} 70) scale(${dockScale}) translate(${-DOCK.x} -70)`}>
              <rect
                x={BOX_X}
                y={32}
                width={BOX_W}
                height={58}
                rx={10}
                fill="hsl(var(--background))"
                stroke={landPulse > 0.04 ? GREEN : "hsl(var(--border))"}
                strokeWidth={landPulse > 0.04 ? 1.5 : 1}
              />
              <text x={398} y={47} className="text-[7.5px] font-semibold" fill="hsl(var(--muted-foreground))">
                {t("withdrawalRules.demo.available")}
              </text>
              <text x={398} y={67} className="text-[15px] font-bold tabular-nums" fill={GREEN_TEXT}>
                {brl(available)}
              </text>
              <text x={398} y={80} className="text-[7px]" fill="hsl(var(--muted-foreground))">
                Liberado para saque
              </text>
            </g>

            {/* onda ao receber um valor */}
            {landPulse > 0.02 && !reduced && (
              <circle
                cx={DOCK.x}
                cy={70}
                r={10 + landPulse * 26}
                fill="none"
                stroke={GREEN}
                strokeWidth={1.2}
                opacity={0.4 * (1 - landPulse)}
              />
            )}

            {/* A liberar */}
            <rect x={BOX_X} y={98} width={BOX_W} height={52} rx={10} fill="hsl(var(--background))" stroke="hsl(var(--border))" />
            <text x={398} y={113} className="text-[7.5px] font-semibold" fill="hsl(var(--muted-foreground))">
              {t("withdrawalRules.demo.toRelease")}
            </text>
            <text x={398} y={132} className="text-[13px] font-bold tabular-nums" fill="hsl(var(--muted-foreground))">
              {brl(toRelease)}
            </text>
            <text x={398} y={143} className="text-[7px]" fill="hsl(var(--muted-foreground))">
              {t("withdrawalRules.demo.stillOnTime")}
            </text>

            {/* Já retirado / Sacado */}
            <rect x={BOX_X} y={158} width={BOX_W} height={46} rx={10} fill="hsl(var(--background))" stroke="hsl(var(--border))" />
            <text x={398} y={171} className="text-[7.5px] font-semibold" fill="#D97706">
              Total retirado
            </text>
            <text x={398} y={189} className="text-[12px] font-bold tabular-nums" fill="#B45309">
              {brl(withdrawn)}
            </text>
            <text x={398} y={198} className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
              Saques na conta bancária
            </text>

            {/* Limite semanal */}
            <g style={{ opacity: finished || reduced ? 1 : 0.85, transition: "opacity 0.3s ease" }}>
              <rect x={BOX_X} y={212} width={BOX_W} height={24} rx={8} fill="hsl(var(--secondary))" />
              <text x={439} y={222.5} textAnchor="middle" className="text-[7.5px] font-semibold" fill="hsl(var(--foreground))">
                {t("withdrawalRules.demo.limit")}
              </text>
              <text x={439} y={231} textAnchor="middle" className="text-[6.5px]" fill="hsl(var(--muted-foreground))">
                {t("withdrawalRules.demo.perProducer")}
              </text>

              <rect x={BOX_X} y={242} width={BOX_W} height={22} rx={7} fill="hsl(var(--primary))" />
              <text x={439} y={255} textAnchor="middle" className="text-[7.5px] font-bold" fill="hsl(var(--primary-foreground))">
                Solicitar saque
              </text>
            </g>

            {/* Moedas — por último, para pousarem por cima do painel */}
            {coins.map((c, i) => {
              const s = SALES[i];
              if (c.opacity <= 0.01) return null;
              const isPix = s.lane === "pix";
              const color = isPix ? "hsl(var(--primary))" : s.lane === "card" ? CARD_COLOR : BOLETO_COLOR;
              const waiting = c.phase === "waiting";
              return (
                <g
                  key={i}
                  transform={`translate(${c.x} ${c.y}) scale(${c.scale})`}
                  opacity={c.opacity}
                  filter="url(#wdShadow)"
                >
                  <rect
                    x={-21}
                    y={-9}
                    width={42}
                    height={18}
                    rx={9}
                    fill="hsl(var(--background))"
                    stroke={color}
                    strokeWidth={1.3}
                    strokeOpacity={waiting ? 0.5 : 1}
                  />
                  <text y={3} textAnchor="middle" className="text-[7.5px] font-bold tabular-nums" fill="hsl(var(--foreground))">
                    {brl(s.value)}
                  </text>
                  {waiting && isPix && (
                    <g transform="translate(-21 12)">
                      <rect width={42} height={3} rx={1.5} fill="hsl(var(--border))" />
                      <rect width={42 * c.progress} height={3} rx={1.5} fill={color} />
                    </g>
                  )}
                  {waiting && !isPix && <Lock opacity={c.locked} />}
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* Legenda rápida */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" /> PIX (D+2)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CARD_COLOR }} /> Cartão (D+1 pós-evento)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BOLETO_COLOR }} /> Boleto (D+1 pós-evento)
          </span>
          <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Saque realizado
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Arraste o cursor para simular diferentes momentos da venda.
        </p>
      </div>
    </div>
  );
}
